import React, { useEffect, useState } from "react";
import styles from "./UtilityBar.module.scss";
import {
  ButtonShape,
  ButtonSize,
  ButtonType,
} from "../../../enums/ButtonAttributesEnums";
import TNButton from "../../../reusable-components/TNButton";
import { InputfieldTypes } from "../../../enums/InputFieldAttributesEnums";
import InputField from "../../../reusable-components/TNInputField";
import SearchIcon from "../../../assets/images/search-button.svg";
import FilterIcon from "../../../assets/images/filterIcon.svg";
import { utilityBarConsts } from "./constants";
import { TriggerType } from "../../../enums/TriggerTypeEnums";
import ConditionalRender from "../../../reusable-components/ConditionalRender";
import SocketLoader from "../../../reusable-components/SocketLoader";
import { socketActionType } from "../../../enums/socketEnums";
import { useFormContext } from "react-hook-form";
import CustomerDatabaseService from "../../../services/customerDatabase.service";
import { useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "../../../constants/QueryKeys";
import { message } from "antd";

interface UtilityBarProps {
  handleFilterDrawerOpen: () => void;
  handleAddCustomerOpen: () => void;
  handleSetSearchValue: (searchValue: string) => void;
  filterCount: number;
  handleClearAllFilter: () => void;
}

const UtilityBar = ({
  handleFilterDrawerOpen,
  handleAddCustomerOpen,
  handleSetSearchValue,
  filterCount,
  handleClearAllFilter,
}: UtilityBarProps) => {
  const { getValues } = useFormContext();
  const queryClient = useQueryClient();
  const [syncAction, setSyncAction] = useState<boolean>(false);
  const { SyncCustomerDatabaseList } = CustomerDatabaseService();
  const { mutateAsync: syncDatabase, isPending } = SyncCustomerDatabaseList();

  const handleSyncActionStart = () => setSyncAction(true);
  const handleSyncActionEnd = () => setSyncAction(false);

  const handleSyncStart = async () => {
    await syncDatabase();
  };

  const handleSyncCallback = (data) => {
    message.success(data);
    queryClient.invalidateQueries([QueryKeys.GET_CUSTOMER_DATABASE_KEY]);
  };

  const addOnAfter = (
    <TNButton
      shape={ButtonShape.DEFAULT}
      size={ButtonSize.SMALL}
      block
      text={
        <img
          className={styles.searchFieldIcon}
          src={SearchIcon}
          alt={utilityBarConsts.SEARCH_ICON_ALT}
        />
      }
      onClick={() => {
        handleSetSearchValue(getValues(utilityBarConsts.SEARCH_FIELD_NAME));
      }}
    />
  );

  return (
    <>
      <ConditionalRender visible={syncAction}>
        <SocketLoader
          actionType={socketActionType.SYNC_FOR_CUSTOMER_DATABASE}
          handleActionCall={handleSyncStart}
          closeRender={handleSyncActionEnd}
          handleCallback={handleSyncCallback}
        />
      </ConditionalRender>
      <div className={styles.headerSection}>
        <div className={styles.leftContainer}>
          <div className={styles.invoiceSearch}>
            <InputField
              type={InputfieldTypes.TEXT}
              name={utilityBarConsts.SEARCH_FIELD_NAME}
              placeholder={utilityBarConsts.SEARCH_PLACEHOLDER}
              addonAfter={addOnAfter}
              onKeyDown={(e) => {
                if (e.key === TriggerType.ENTER) {
                  e.preventDefault();
                  handleSetSearchValue(
                    getValues(utilityBarConsts.SEARCH_FIELD_NAME),
                  );
                }
              }}
            />
          </div>
          <TNButton
            filterButton
            shape={ButtonShape.DEFAULT}
            size={ButtonSize.LARGE}
            badgeCount={filterCount}
            text="Filter"
            afterTitle={true}
            icon={<img src={FilterIcon} />}
            onClick={() => handleFilterDrawerOpen()}
          />
          {filterCount > 0 && (
            <TNButton
              clearFilterButton
              shape={ButtonShape.ROUND}
              size={ButtonSize.MEDIUM}
              text="Clear All"
              onClick={handleClearAllFilter}
            />
          )}
        </div>
        <div className={styles.rightContainer}>
          <TNButton
            className={styles.syncButton}
            shape={ButtonShape.DEFAULT}
            size={ButtonSize.LARGE}
            type={ButtonType.DEFAULT}
            onClick={() => handleSyncActionStart()}
            text={utilityBarConsts.SYNC_FROM_QUICKBOOKS}
          />
          <TNButton
            className={styles.addCustomerButton}
            shape={ButtonShape.DEFAULT}
            size={ButtonSize.LARGE}
            text={
              <div className={styles.addCustomerText}>
                {utilityBarConsts.ADD_CUSTOMER_BTN}
              </div>
            }
            onClick={() => handleAddCustomerOpen()}
          />
        </div>
      </div>
    </>
  );
};

export default UtilityBar;
